import React, { PropsWithChildren, useState } from 'react';
import { useTheme } from 'styled-components';
import * as S from './BottomSheet.styles';
import { CloseIcon } from '@/components/UI/Icons/Icons';
import { Portal } from '@/components/UI/Portal/Portal';
import { Overlay } from '@/components/UI/Overlay';
import { useBodyOverflow } from '@/hooks/useBodyOverflow';
import { useAnimatedVisibility } from '@/hooks/useAnimatedVisibility';
import { useModalKeyboard } from '@/hooks/useModalKeyboard';

interface BottomSheetProps {
  isOpen: boolean;
  close: () => void;
  title?: string;
  headPaddingTop?: number;
  hasHeadBorder?: boolean;
  isTitleCentered?: boolean;
  isInPortal?: boolean;
  dataTestId?: string;
  hasOverlay?: boolean;
  isPageLocked?: boolean;
  onCloseClick?: () => void;
  background?: string;
  shellBackground?: string;
}

export const BottomSheet = ({
  children,
  isOpen,
  close,
  title,
  headPaddingTop = 0,
  isPageLocked = false,
  isTitleCentered = true,
  hasHeadBorder = true,
  hasOverlay = true,
  isInPortal,
  dataTestId,
  onCloseClick,
  background,
  shellBackground,
}: PropsWithChildren<BottomSheetProps>) => {
  const { isVisible, onAnimationEnd } = useAnimatedVisibility(isOpen);
  const theme = useTheme();
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
  const { isKeyboardOpen } = useModalKeyboard(isVisible, bodyEl);
  useBodyOverflow(isVisible && isPageLocked);

  function blurActiveField() {
    const active = document.activeElement;

    if (active instanceof HTMLElement && bodyEl?.contains(active)) {
      active.blur();
    }
  }

  function dismissOverlay() {
    blurActiveField();
    close();
  }

  function dismissSheet() {
    blurActiveField();
    (onCloseClick ?? close)();
  }

  function renderBottomSheet() {
    return (
      <>
        {hasOverlay && <Overlay isOpen={isOpen} onClick={dismissOverlay} />}
        <S.Box
          $isOpen={isOpen}
          $isKeyboardOpen={isKeyboardOpen}
          $background={shellBackground || background}
          data-testid={dataTestId ?? 'bottom-sheet'}
          onAnimationEnd={onAnimationEnd}
        >
          {title && (
            <S.Head
              $headPaddingTop={headPaddingTop}
              $isTitleCentered={isTitleCentered}
              $hasBorder={hasHeadBorder}
              $background={background}
            >
              <S.Title $variant="3">{title}</S.Title>
              <S.Close
                appearance="transparent"
                onPointerDown={e => e.preventDefault()}
                onClick={dismissSheet}
              >
                <CloseIcon fill={theme.palette.secondary.labels} />
              </S.Close>
            </S.Head>
          )}
          <S.Body ref={setBodyEl} $background={background}>
            {children}
          </S.Body>
        </S.Box>
      </>
    );
  }

  if (!isVisible) return null;

  if (isInPortal) {
    return <Portal>{renderBottomSheet()}</Portal>;
  }

  return renderBottomSheet();
};
